import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Flag, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PitchTimePicker } from '../retention/PitchTimePicker';
import { parseTimeToSeconds } from './utils';
import { metricsService } from '@/services/metricsService';
import { useAuth } from '@/hooks/useAuth';
import type { PitchInput, VideoPitch } from '@/types/pitch';
import {
  MAX_PITCHES,
  MAX_PITCH_NAME_LENGTH,
  formatPitchTime,
  knownDuration,
  pitchLabel,
  pitchSaveErrorMessage,
  removePitch,
  upsertPitch,
  validatePitches,
} from '@/utils/pitches';

interface PitchQuickModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Vídeo dono dos pitches — é nele que o PUT grava. */
  videoId: string | null;
  /** Lista atual COMPLETA do vídeo: o PUT substitui a lista inteira. */
  pitches: VideoPitch[];
  /** Pitch em edição; null = adicionar um pitch novo. */
  editingPitchId: string | null;
  /** Duração do vídeo em segundos (teto do tempo do pitch). */
  durationInSeconds?: number | null;
  /** Recebe a lista salva; o pai refaz o fetch das métricas. */
  onSaved?: (pitches: VideoPitch[]) => void | Promise<void>;
}

export function PitchQuickModal({
  isOpen,
  onClose,
  videoId,
  pitches,
  editingPitchId,
  durationInSeconds,
  onSaved,
}: PitchQuickModalProps) {
  useLanguage();
  const [pitchTime, setPitchTime] = useState('00:00');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');

  const editingIndex = editingPitchId ? pitches.findIndex((p) => p.id === editingPitchId) : -1;
  const editing = editingIndex >= 0 ? pitches[editingIndex] : null;
  const editingSeconds = editing?.seconds ?? null;
  const editingName = editing?.name ?? '';

  useEffect(() => {
    if (isOpen && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para editar pitches"));
      onClose();
    }
  }, [isOpen, canEdit, onClose]);

  // A cada abertura: preenche com o pitch em edição ou zera para um novo.
  useEffect(() => {
    if (!isOpen) return;
    setPitchTime(editingSeconds != null ? formatPitchTime(editingSeconds) : '00:00');
    setName(editingName);
    setError(null);
  }, [isOpen, editingSeconds, editingName]);

  const persist = async (list: PitchInput[], successMessage: string) => {
    if (!videoId) {
      toast.error(translateInterfaceText("Vídeo não encontrado para salvar o pitch."));
      return;
    }
    setIsSaving(true);
    try {
      const saved = await metricsService.updatePitches(videoId, list);
      await onSaved?.(saved);
      toast.success(successMessage);
      onClose();
    } catch (err) {
      console.error('Erro ao salvar pitches:', err);
      toast.error(pitchSaveErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    const list = upsertPitch(pitches, editing ? editing.id : null, {
      name,
      seconds: parseTimeToSeconds(pitchTime),
    });
    const validationError = validatePitches(list, durationInSeconds);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    await persist(list, editing ? 'Pitch atualizado.' : 'Pitch adicionado.');
  };

  const handleRemove = async () => {
    if (!editing) return;
    await persist(removePitch(pitches, editing.id), 'Pitch removido.');
  };

  // Enter salva só a partir dos campos — num botão focado (ex.: "Remover pitch")
  // o Enter tem que acionar o próprio botão.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSaving && (e.target as HTMLElement).tagName === 'INPUT') {
      e.preventDefault();
      handleSave();
    }
  };

  const title = editing ? `Editar ${pitchLabel(editing, editingIndex)}` : translateInterfaceText("Adicionar pitch");

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md rounded-[14px]" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold flex items-center gap-2">
            <Flag className="h-5 w-5 text-[#2D66F5]" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#6B7280]">
            {translateInterfaceText("Defina o momento em que a oferta aparece no vídeo. Cada vídeo pode ter até")}{" "}{MAX_PITCHES} {" "}{translateInterfaceText("pitches.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <p className="text-[13px] font-medium text-[#2C3345]">{translateInterfaceText("Tempo do pitch")}</p>
            <PitchTimePicker
              value={pitchTime}
              onChange={(value) => {
                setPitchTime(value);
                setError(null);
              }}
              onClear={() => setPitchTime('00:00')}
              // Duração desconhecida (0 ou o default 1 do schema) não pode travar o campo.
              videoLength={knownDuration(durationInSeconds) ?? undefined}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pitch-name" className="text-[13px] text-[#2C3345]">
              {translateInterfaceText("Nome (opcional)")}</Label>
            <Input
              id="pitch-name"
              value={name}
              maxLength={MAX_PITCH_NAME_LENGTH}
              placeholder={translateInterfaceText("Ex.: Oferta principal")}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
            />
          </div>

          {error && (
            <p role="alert" className="text-[13px] font-medium text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 pt-3 border-t border-[#ECEEF2] sm:justify-between">
          {editing ? (
            <Button
              variant="ghost"
              onClick={handleRemove}
              disabled={isSaving}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              {translateInterfaceText("Remover pitch")}</Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
              className="hover:bg-muted hover:text-foreground"
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? translateInterfaceText("Salvando...") : translateInterfaceText("Salvar")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
